import React, { memo, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import useDeepCompareEffect from 'use-deep-compare-effect';

import TestPurchaseDiscardedReason from 'redpoints-model-js/model/testpurchase/reason/TestPurchaseDiscardedReason';

import {
  Button,
  Col,
  Loader,
  Notification,
  Paper,
  Row,
} from 'redpoints-front-components/index';
import { withContext } from 'redpoints-front-common/components';

import {
  testPurchaseCompleteEvidence,
  testPurchaseDelayLink,
  testPurchaseDiscardEvidence,
  testPurchaseFetchLinkInfo,
} from '../../../actions/testPurchaseActions';
import {
  getTestPurchaseFiltersSelected,
  getTestPurchaseLinkInfoActionIsLoading,
  getTestPurchaseLinkInfoActionIsSuccess,
  getTestPurchaseLinkInfoContext,
  getTestPurchaseLinkInfoEvidence,
  getTestPurchaseLinkInfoLinkId,
} from '../../../reducers/tasks/testPurchase';
import TestPurchaseDiscardedReasonsModal from './TestPurchaseDiscardedReasonsModal';
import { translate } from '../../../helpers/languageHelper';
import { getContextDataFromFilterBy } from './testPurchaseFiltersHelpers';

import './testPurchaseActions.less';

const TestPurchaseActions = ({
  context,
  evidence,
  filtersSelected,
  isActionSuccess,
  isLoading,
  linkId,
  linkInfoContext,
  testPurchaseCompleteEvidence,
  testPurchaseDelayLink,
  testPurchaseDiscardEvidence,
  testPurchaseFetchLinkInfo,
}) => {
  const hasEvidencesUrl = Boolean(evidence?.evidencesUrl?.length);

  const [showDiscardedReasonsModal, setShowDiscardedReasonsModal] = useState(
    false
  );

  useDeepCompareEffect(() => {
    const { allDomainIds, filterBy } = filtersSelected;
    const { customerId, contractId } = getContextDataFromFilterBy(
      filterBy,
      context
    );

    if (isActionSuccess) {
      testPurchaseFetchLinkInfo({
        allDomainIds: allDomainIds,
        contractId,
        customerId,
      });
    }
  }, [filtersSelected, isActionSuccess, isLoading, testPurchaseFetchLinkInfo]);

  const handleClickDelay = () => {
    testPurchaseDelayLink({ linkId, context: linkInfoContext });
  };

  const handleClickAllow = () => {
    if (hasEvidencesUrl) {
      testPurchaseCompleteEvidence({
        linkId,
        evidence,
        context: linkInfoContext,
      });
    } else {
      Notification.error({
        message: translate('tasks.testPurchase.actions.validationError'),
      });
    }
  };

  const handleAcceptModal = selectedReason => {
    evidence.discardedReason = Object.assign(
      new TestPurchaseDiscardedReason(),
      selectedReason
    );
    testPurchaseDiscardEvidence({ linkId, evidence, context: linkInfoContext });
  };

  const handleClickDiscard = () => {
    setShowDiscardedReasonsModal(true);
  };

  const handleHideDiscardedReasonsModal = () => {
    setShowDiscardedReasonsModal(false);
  };

  const areButtonsDisabled = isActionSuccess || isLoading;

  return (
    <Paper className="test-purchase-actions">
      <Loader isLoading={isLoading} />
      <Row gutter={16}>
        <Col flex="auto">
          <Button
            aria-label="button-delay"
            disabled={areButtonsDisabled}
            onClick={handleClickDelay}
            size="large"
            type="warning"
          >
            {translate('tasks.testPurchase.actions.delay')}
          </Button>
          <Button
            aria-label="button-allow"
            disabled={areButtonsDisabled}
            onClick={handleClickAllow}
            size="large"
            type="primary"
          >
            {translate('tasks.testPurchase.actions.allow')}
          </Button>
        </Col>
        <Col flex="none">
          <Button
            aria-label="button-discard"
            disabled={areButtonsDisabled}
            onClick={handleClickDiscard}
            size="large"
            type="danger"
          >
            {translate('tasks.testPurchase.actions.discard')}
          </Button>
        </Col>
      </Row>
      {showDiscardedReasonsModal && (
        <TestPurchaseDiscardedReasonsModal
          onAccept={handleAcceptModal}
          onCancel={handleHideDiscardedReasonsModal}
        />
      )}
    </Paper>
  );
};

const mapStateToProps = (state, { context }) => ({
  evidence: getTestPurchaseLinkInfoEvidence(state),
  filtersSelected: getTestPurchaseFiltersSelected(state),
  isActionSuccess: getTestPurchaseLinkInfoActionIsSuccess(state),
  isLoading: getTestPurchaseLinkInfoActionIsLoading(state),
  linkId: getTestPurchaseLinkInfoLinkId(state),
  linkInfoContext: getTestPurchaseLinkInfoContext(state, context),
});

export default compose(
  withContext,
  connect(mapStateToProps, {
    testPurchaseCompleteEvidence,
    testPurchaseDelayLink,
    testPurchaseDiscardEvidence,
    testPurchaseFetchLinkInfo,
  }),
  memo
)(TestPurchaseActions);
